import React from 'react';
import { ResponsiveTooltip, Typography } from '../../../../components';
import { InfoCircle } from '../../../../components/icons';
import { RevshareOffer } from '../../../../api/client/clientRevshareService';
import { useTranslations } from '../../../../contexts/translation.context';
import { formatTimeAgo } from '../../../../utils/formatTimeAgo';
import IcDate from '../../../../components/ic-date.svg';

interface StatisticsCellProps {
  offer: RevshareOffer;
}

interface ArpuTooltipContentProps {
  transactions: number;
  subscribers: number;
  t: (key: string) => string;
}

const ArpuTooltipContent: React.FC<ArpuTooltipContentProps> = ({ transactions, subscribers, t }) => {
  return (
    <div className="bg-white border border-light rounded-3 shadow-[0px_0px_11px_0px_rgba(0,0,0,0.1)] p-4 min-w-[12.5rem]">
      <div className="mb-2">
        <Typography variant="body2" className="text-auth-ink">
          {t('revshare.table.arpu.title')}
        </Typography>
      </div>
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between py-1.5">
          <Typography variant="label3" className="text-neutral-600">
            {t('revshare.table.arpu.transactions')}
          </Typography>
          <Typography variant="label3" className="text-zinc-700 font-medium">
            ${transactions.toFixed(2)}
          </Typography>
        </div>
        <div className="flex items-center justify-between py-1.5">
          <Typography variant="label3" className="text-neutral-600">
            {t('revshare.table.arpu.subscribers')}
          </Typography>
          <Typography variant="label3" className="text-zinc-700 font-medium">
            {subscribers}
          </Typography>
        </div>
      </div>
    </div>
  );
};

const StatisticsCell: React.FC<StatisticsCellProps> = ({ offer }) => {
  const { t } = useTranslations();

  const transactions = parseFloat(String(offer.ref_share_transactions_total ?? '0')) || 0;
  const subscribers = offer.ref_share_subscribers_total ?? 0;
  const arpu = subscribers > 0 ? transactions / subscribers : 0;
  const activeCampaigns = offer.ref_share_active_total ?? offer.revshare_campaigns_count ?? 0;
  const createdAt = offer.created_at || new Date().toISOString();

  const activeCampaignsText = t('revshare.table.activeCampaigns');

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1">
        <Typography variant="body3" className="text-zinc-500 text-xs leading-4">
          ARPU:
        </Typography>
        <Typography variant="body3" className="text-auth-ink font-semibold text-sm leading-4">
          ${arpu.toFixed(2)}
        </Typography>
        <ResponsiveTooltip
          content={<ArpuTooltipContent transactions={transactions} subscribers={subscribers} t={t} />}
          placement="bottom"
          showArrow={false}
          classNames={{
            content: 'p-0 bg-transparent shadow-lg',
          }}
        >
          <span className="cursor-pointer flex items-center">
            <InfoCircle size={14} />
          </span>
        </ResponsiveTooltip>
      </div>

      <div className="flex items-center gap-1">
        <Typography variant="body3" className="text-zinc-500 text-xs leading-4">
          {typeof activeCampaignsText === 'string' ? activeCampaignsText : 'Активные кампании'}:
        </Typography>
        <Typography variant="body3" className="text-auth-ink font-semibold text-sm leading-4">
          {activeCampaigns}
        </Typography>
      </div>

      <div className="flex items-center gap-1">
        <img src={IcDate} alt="date" className="w-3.5 h-3.5" />
        <Typography variant="body3" className="text-zinc-500 text-xs leading-4">
          {formatTimeAgo(createdAt, t)}
        </Typography>
      </div>
    </div>
  );
};

export default StatisticsCell;
